/**
 * Local attachments: the receipt image/PDF (≤ 2000 px JPEG or the PDF) and a
 * thumbnail, stored in IndexedDB next to the inbox item and referenced by the
 * verifikation's `attachmentIds`. The Drive copy is the legal archive; the
 * local blob may be dropped later (`dropLocalBlob`) while the thumbnail and
 * the hash stay.
 *
 * Image processing lives in `media/image.ts`; it is injected here so the
 * domain has no dependency on it. Without helpers the original is stored and
 * doubles as its own thumbnail.
 */
import Dexie from 'dexie';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { MediaHelpers } from '../inbox/internal-types';
import type { Attachment, Uuid } from '../types';
import { sha256Hex } from './hash';

/** Warn when the origin's storage is this full. */
export const STORAGE_WARN_RATIO = 0.8;

let defaultMedia: MediaHelpers = {};

/** Wire `media/image` helpers once at app start (tests pass helpers per call instead). */
export function configureAttachmentMedia(helpers: MediaHelpers): void {
  defaultMedia = { ...helpers };
}

/**
 * File names of a company's attachments, keyed by attachment id, for the
 * Händelser search ("hitta fakturan som hette telia-mars.pdf").
 *
 * Read straight off the `[companyId+name]` index with a key-only cursor: the
 * rows themselves hold the receipt image and its thumbnail, so loading them
 * would pull every stored blob of the company into memory just to search on a
 * file name.
 */
export async function attachmentNames(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Map<Uuid, string>> {
  const names = new Map<Uuid, string>();
  await db.attachments
    .where('[companyId+name]')
    .between([companyId, Dexie.minKey], [companyId, Dexie.maxKey])
    .eachKey((key, cursor) => {
      const name = (key as [Uuid, string])[1];
      if (name) names.set(cursor.primaryKey as Uuid, name);
    });
  return names;
}

export interface StoreAttachmentInput {
  companyId: Uuid;
  blob: Blob;
  name: string;
  driveFileId?: string | undefined;
  /** Skip shrinking (the phone already shrank it, or it is a PDF). */
  alreadyShrunk?: boolean | undefined;
}

export interface StoreAttachmentResult {
  attachment: Attachment;
  /** True when an attachment with the same bytes already existed for the company. */
  duplicate: boolean;
  /**
   * The duplicate had no local bytes — a record restored from a backup whose
   * file has not been fetched back — and these are them, now stored on it.
   */
  restoredBytes: boolean;
}

const PDF_MIME = 'application/pdf';

function mediaTypeOf(blob: Blob, name: string): string {
  if (blob.type) return blob.type;
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'pdf':
      return PDF_MIME;
    default:
      return 'application/octet-stream';
  }
}

/** Shrink (images only) and make a thumbnail; every step falls back to the original. */
export async function prepareBlobs(blob: Blob, media: MediaHelpers, alreadyShrunk = false): Promise<{ original: Blob; thumbnail: Blob }> {
  let original = blob;
  if (!alreadyShrunk && media.shrinkImage && blob.type !== PDF_MIME) {
    try {
      original = await media.shrinkImage(blob);
    } catch {
      original = blob;
    }
  }
  let thumbnail = original;
  if (media.makeThumbnail) {
    try {
      thumbnail = await media.makeThumbnail(original);
    } catch {
      thumbnail = original;
    }
  }
  return { original, thumbnail };
}

/**
 * Store a file for a company. The hash is taken over the bytes as stored
 * (after shrinking), so a phone capture and its Drive round-trip hash the
 * same. An identical file already stored for the company is returned as a
 * duplicate instead of being stored twice.
 */
export async function storeAttachment(
  input: StoreAttachmentInput,
  media: MediaHelpers = defaultMedia,
  db: BookkeepingDB = defaultDb,
): Promise<StoreAttachmentResult> {
  const { original, thumbnail } = await prepareBlobs(input.blob, media, input.alreadyShrunk);
  const sha256 = await sha256Hex(original);
  return db.transaction('rw', db.attachments, async () => {
    const existing = await db.attachments.where('sha256').equals(sha256).and((a) => a.companyId === input.companyId).first();
    if (existing) {
      const patch: Partial<Attachment> = {};
      if (input.driveFileId && !existing.driveFileId) patch.driveFileId = input.driveFileId;
      /*
       * A restored backup carries every attachment as a record without its
       * bytes, and a file the hydration could not fetch stays that way. The
       * user has just handed us those exact bytes — the hash says so — and
       * handing back the empty record instead would send them round again
       * ("ladda upp kvittot igen") for ever.
       */
      const restoredBytes = existing.blob === null;
      if (restoredBytes) Object.assign(patch, { blob: original, thumbnail, hasLocalBlob: 1 as const, bytes: original.size });
      if (Object.keys(patch).length) await db.attachments.update(existing.id, patch);
      return { attachment: { ...existing, ...patch }, duplicate: true, restoredBytes };
    }
    const attachment: Attachment = {
      id: crypto.randomUUID(),
      companyId: input.companyId,
      name: input.name.trim() || 'kvitto',
      mediaType: mediaTypeOf(original, input.name),
      bytes: original.size,
      blob: original,
      hasLocalBlob: 1,
      thumbnail,
      ...(input.driveFileId ? { driveFileId: input.driveFileId } : {}),
      sha256,
      createdAt: new Date().toISOString(),
    };
    await db.attachments.add(attachment);
    return { attachment, duplicate: false, restoredBytes: false };
  });
}

export async function getAttachment(id: Uuid, db: BookkeepingDB = defaultDb): Promise<Attachment | undefined> {
  return db.attachments.get(id);
}

export async function listAttachments(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Attachment[]> {
  return db.attachments.where('companyId').equals(companyId).toArray();
}

/** Remember which Drive file holds this attachment (after upload or filing). */
export async function setAttachmentDriveFile(id: Uuid, driveFileId: string, db: BookkeepingDB = defaultDb): Promise<void> {
  await db.attachments.update(id, { driveFileId });
}

/**
 * Last resort under a full browser quota: drop the local original, keeping the
 * thumbnail. Refuses when no remote copy exists, since the local blob would
 * then be the only one.
 *
 * This breaks the rule that the local database is the master copy, so nothing
 * calls it automatically and nothing should: a receipt that exists only in a
 * remote makes the primary store the incomplete one, and the hydration has to
 * fetch it back before the AI or a re-upload can use it. Offer it as an
 * explicit choice when storage is actually full, never as housekeeping.
 */
export async function dropLocalBlob(id: Uuid, db: BookkeepingDB = defaultDb): Promise<boolean> {
  const a = await db.attachments.get(id);
  if (!a || !a.driveFileId || a.blob === null) return false;
  await db.attachments.update(id, { blob: null, hasLocalBlob: 0 });
  return true;
}

/** Bytes of the local original (for the AI read or a re-upload); undefined when only Drive has it. */
export async function attachmentBytes(id: Uuid, db: BookkeepingDB = defaultDb): Promise<{ bytes: Uint8Array; mediaType: string } | undefined> {
  const a = await db.attachments.get(id);
  if (!a?.blob) return undefined;
  return { bytes: new Uint8Array(await a.blob.arrayBuffer()), mediaType: a.mediaType };
}

// ---- Storage quota --------------------------------------------------------------

export interface StorageStatus {
  usage: number;
  quota: number;
  /** 0–1; 0 when the quota is unknown. */
  ratio: number;
  warn: boolean;
  /** Swedish sentence for a callout when `warn` is true. */
  message: string | null;
}

/** Pure: the warning decision and copy for a usage/quota pair. */
export function storageStatusOf(usage: number, quota: number): StorageStatus {
  const ratio = quota > 0 ? usage / quota : 0;
  const warn = ratio >= STORAGE_WARN_RATIO;
  const percent = Math.round(ratio * 100);
  return {
    usage,
    quota,
    ratio,
    warn,
    message: warn
      ? `Lagringsutrymmet i webbläsaren är nästan fullt (${percent} %). Bokföringen ligger kvar, men nya kvitton kan sluta gå att spara. Kontrollera att arkivet är säkerhetskopierat under Mer › Lagring.`
      : null,
  };
}

/** `navigator.storage.estimate()` wrapped; unknown quota → no warning. */
export async function storageStatus(nav: { storage?: { estimate?: () => Promise<{ usage?: number; quota?: number }> } } | undefined = globalThis.navigator): Promise<StorageStatus> {
  try {
    const estimate = nav?.storage?.estimate ? await nav.storage.estimate() : undefined;
    return storageStatusOf(estimate?.usage ?? 0, estimate?.quota ?? 0);
  } catch {
    return storageStatusOf(0, 0);
  }
}
